// Native Horizon blocks share a product, while this collection represents one colour per tile.
// Bind every child to the Liquid-selected variant, including after facet/section replacement.
const tileSelector = '.product-grid__item--color-variant';

// Explicitly requested on 9 October: mix colours by default, retaining native explicit sorts.
export function mixColorCards(cards, random = Math.random, previousProduct = null) {
  const pool = [...cards];
  for (let i = pool.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [pool[i], pool[j]] = [pool[j], pool[i]];
  }
  const counts = new Map();
  pool.forEach((card) => counts.set(card.dataset.productId, (counts.get(card.dataset.productId) ?? 0) + 1));
  const mixed = [];
  while (pool.length) {
    const remaining = pool.length - 1;
    // Keep a feasible suffix so a shuffled choice cannot strand identical products together.
    let index = pool.findIndex((card) => card.dataset.productId !== previousProduct && [...counts].every(([id, count]) => {
      const after = count - Number(id === card.dataset.productId);
      return after <= (id === card.dataset.productId ? Math.floor(remaining / 2) : Math.ceil(remaining / 2));
    }));
    if (index < 0) index = pool.findIndex((card) => card.dataset.productId !== previousProduct);
    if (index < 0) index = 0; // A one-product collection cannot separate its own colours.
    const [card] = pool.splice(index, 1);
    previousProduct = card.dataset.productId;
    counts.set(previousProduct, counts.get(previousProduct) - 1);
    mixed.push(card);
  }
  return mixed;
}

export function mixFreshColorCards(results, { explicitSort = false, random = Math.random } = {}) {
  if (explicitSort || results.getAttribute('data-randomize-color-cards') !== 'true') return;
  results.querySelectorAll('.product-grid').forEach((grid) => {
    const batches = new Map();
    [...grid.children].filter((card) => card.matches('.product-grid__item') && card.dataset.colorCardMixed !== 'true').forEach((card) => {
      const page = card.dataset.page ?? '1';
      if (!batches.has(page)) batches.set(page, []);
      batches.get(page).push(card);
    });
    batches.forEach((cards) => {
      const previous = cards[0].previousElementSibling?.dataset.productId ?? null;
      const mixed = mixColorCards(cards, random, previous);
      // Move the original nodes into their original slots; preserve page boundaries and card state.
      const slots = cards.map((card) => {
        card.dataset.colorCardMixed = 'true';
        const slot = document.createComment('colour-card-slot');
        card.before(slot);
        return slot;
      });
      slots.forEach((slot, index) => { slot.replaceWith(mixed[index]); });
    });
  });
}

export function hydrateColorCard(tile) {
  const { variantUrl, variantId } = tile.dataset;
  // Hyphens before digits are preserved by DOMStringMap: variantImage1200 is not this attribute.
  const variantImage400 = tile.getAttribute('data-variant-image-400');
  const variantImage800 = tile.getAttribute('data-variant-image-800');
  const variantImage1200 = tile.getAttribute('data-variant-image-1200');
  if (!variantUrl || !variantId) return;

  tile.querySelectorAll('a[href*="/products/"]').forEach((link) => {
    if (link.getAttribute('href') !== variantUrl) link.setAttribute('href', variantUrl);
    if (link.hasAttribute('data-product-href')) link.setAttribute('data-product-href', variantUrl);
  });
  tile.querySelectorAll('product-card').forEach((card) => {
    card.dataset.selectedVariantId = variantId;
  });
  tile.querySelectorAll('.product-card__wishlist-btn[data-variant-id]').forEach((element) => {
    element.dataset.variantId = variantId;
  });
  // Never change option-picker IDs: each size keeps its own inventory and merchandise ID.
  tile.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
    input.value = variantId;
    input.setAttribute('value', variantId);
  });

  const gallery = tile.querySelector('.card-gallery');
  const slideshow = gallery?.querySelector('slideshow-component');
  const slides = Array.from(gallery?.querySelectorAll('slideshow-slide') ?? []);
  const firstSlide = slides[0];
  const image = firstSlide?.querySelector('img') ?? gallery?.querySelector('img');

  if (gallery) {
    gallery.removeAttribute('on:pointerenter');
    gallery.removeAttribute('on:pointerleave');
    // Other product media can belong to other colours. A colour tile shows its assigned image.
    slideshow?.setAttribute('disabled', 'true');
    slideshow?.setAttribute('data-generic-media-size', '1');
    slides.slice(1).forEach((slide) => {
      slide.setAttribute('hidden', '');
      slide.setAttribute('aria-hidden', 'true');
    });
    gallery.querySelectorAll('slideshow-arrows, slideshow-controls').forEach((control) => {
      control.setAttribute('hidden', '');
    });
  }

  if (image && variantImage1200) {
    image.removeAttribute('hidden');
    const srcset = [variantImage400 && `${variantImage400} 400w`, variantImage800 && `${variantImage800} 800w`, `${variantImage1200} 1200w`].filter(Boolean).join(', ');
    image.setAttribute('src', variantImage1200);
    image.setAttribute('srcset', srcset);
    image.setAttribute('alt', tile.dataset.variantImageAlt ?? '');
    image.setAttribute('data-src', variantImage1200);
    image.setAttribute('data-srcset', srcset);
    image.closest('picture')?.querySelectorAll('source').forEach((source) => {
      source.setAttribute('srcset', srcset);
      source.setAttribute('data-srcset', srcset);
    });
    firstSlide?.removeAttribute('hidden');
    firstSlide?.removeAttribute('aria-hidden');
    tile.querySelectorAll('product-card-link').forEach((link) => {
      link.dataset.featuredMediaUrl = variantImage1200;
    });
  } else if (image) {
    // Missing colour media must never display another colour as a fallback.
    image.setAttribute('hidden', '');
  }

  tile.querySelectorAll('product-price').forEach((price) => {
    price.querySelectorAll('.price').forEach((amount) => {
      if (tile.dataset.variantPrice && amount.textContent !== tile.dataset.variantPrice) amount.textContent = tile.dataset.variantPrice;
    });
    price.querySelectorAll('.compare-at-price').forEach((amount) => {
      const compare = tile.dataset.variantComparePrice ?? '';
      if (amount.textContent !== compare) amount.textContent = compare;
      amount.closest('[role="group"]')?.toggleAttribute('hidden', !compare);
    });
  });

  const available = tile.dataset.variantAvailable !== 'false';
  const badges = tile.querySelector('.product-badges');
  if (badges && !available) {
    let badge = badges.querySelector('.product-badges__badge');
    if (!badge) {
      badge = document.createElement('div');
      badge.className = 'product-badges__badge product-badges__badge--rectangle';
      badges.append(badge);
    }
    if (badge.textContent !== tile.dataset.variantSoldOutText) badge.textContent = tile.dataset.variantSoldOutText;
    badge.removeAttribute('hidden');
  }
  tile.querySelectorAll('quick-add-component button[name="add"]').forEach((button) => {
    button.disabled = !available;
  });
  tile.dataset.variantCardReady = 'true';
}

export function hydrateColorCards(scope) {
  if (scope.matches?.(tileSelector)) hydrateColorCard(scope);
  scope.querySelectorAll?.(tileSelector).forEach(hydrateColorCard);
}

class SuperaColorCardRuntime extends HTMLElement {
  handleSort = (event) => {
    const input = event.target;
    if (!(input instanceof Element) || input.getAttribute('name') !== 'sort_by' || !this.results.contains(input)) return;
    this.explicitSort = Boolean(input.value);
    if (!this.explicitSort) {
      // Empty is the normal Shopify default; do not send an invented sort value to Shopify.
      event.stopImmediatePropagation();
      const url = new URL(window.location.href);
      url.searchParams.delete('sort_by');
      url.searchParams.delete('page');
      window.location.assign(url.toString());
    }
  };

  connectedCallback() {
    const results = this.closest('results-list');
    if (!results) return;
    this.results = results;
    this.explicitSort = Boolean(new URL(window.location.href).searchParams.get('sort_by'));
    document.addEventListener('change', this.handleSort, true);
    hydrateColorCards(results);
    mixFreshColorCards(results, { explicitSort: this.explicitSort });
    this.observer = new MutationObserver((mutations) => {
      const tiles = new Set();
      mutations.forEach(({ target, addedNodes }) => {
        const tile = target instanceof Element ? target.closest(tileSelector) : null;
        if (tile) tiles.add(tile);
        addedNodes.forEach((node) => {
          if (node instanceof Element) hydrateColorCards(node);
        });
      });
      tiles.forEach(hydrateColorCard);
      mixFreshColorCards(results, { explicitSort: this.explicitSort });
    });
    this.observer.observe(results, { childList: true, subtree: true });
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    document.removeEventListener('change', this.handleSort, true);
  }
}

if (!customElements.get('supera-color-card-runtime')) customElements.define('supera-color-card-runtime', SuperaColorCardRuntime);
